import React, { useState } from 'react';
import {
  Space, Select, Button, Upload, Typography,
  Row, Col, Alert, message, Modal, Card, Steps, Spin
} from 'antd';
import {
  DownloadOutlined, UploadOutlined, ExclamationCircleOutlined,
  FileSearchOutlined, CloudUploadOutlined
} from '@ant-design/icons';
import { useDescargarCsvEjemplo, useImportarCsv, useListarTiposCarga } from './queries';

const { Text, Title } = Typography;
const { Dragger } = Upload;

export const CargaArchivos = () => {
  const [tipo, setTipo] = useState(null);
  const [archivo, setArchivo] = useState(null);

  const { data: opcionesTipo = [], isLoading: cargandoTipos } = useListarTiposCarga();
  const descargar = useDescargarCsvEjemplo();
  const importar = useImportarCsv();

  const step = tipo ? (archivo ? 2 : 1) : 0;

  const handleTipoChange = (value) => {
    setTipo(value ?? null);
    setArchivo(null);
  };

  const beforeUpload = (file) => {
    const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCSV) {
      message.error('Solo se permiten archivos CSV');
      return false;
    }
    setArchivo(file);
    return false;
  };

  const handleDownload = async () => {
    if (!tipo) return;
    await descargar.mutateAsync(tipo);
  };

  const handleUpload = () => {
    if (!tipo || !archivo) return;
    const tipoSeleccionado = opcionesTipo.find(opt => opt.value === tipo);

    Modal.confirm({
      title: '¿Confirmar importación?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Tipo:</strong> {tipoSeleccionado?.label}</p>
          <p><strong>Archivo:</strong> {archivo.name}</p>
          <p><strong>Tamaño:</strong> {(archivo.size / 1024).toFixed(2)} KB</p>
          <p style={{ color: '#ff4d4f' }}>
            <strong>Advertencia:</strong> Esta acción agregará nuevos datos a la base de datos.
          </p>
        </div>
      ),
      okText: 'Importar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await importar.mutateAsync({ tipo, file: archivo });
          setArchivo(null);
        } catch (_) {}
      },
      onCancel() {
        message.info('Importación cancelada');
      },
    });
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Encabezado */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <CloudUploadOutlined style={{ fontSize: 28, color: '#1890ff', marginBottom: 6, display: 'block' }} />
        <Title level={4} style={{ margin: 0 }}>Carga masiva de archivos</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Importa datos desde un archivo CSV al sistema
        </Text>
      </div>

      {/* Indicador de pasos */}
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 28 }}
        items={[
          { title: 'Seleccionar tipo' },
          { title: 'Descargar plantilla' },
          { title: 'Subir archivo' },
        ]}
      />

      <Space direction="vertical" style={{ width: '100%' }} size={20}>

        {/* Paso 1: tipo */}
        <Card
          size="small"
          style={{ borderRadius: 12, border: '1.5px solid #d9d9d9' }}
          bodyStyle={{ padding: '20px 20px 16px' }}
        >
          <Row align="middle" gutter={12} style={{ marginBottom: 12 }}>
            <Col>
              <FileSearchOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            </Col>
            <Col flex="auto">
              <Text strong>1. Selecciona el tipo de datos</Text>
            </Col>
          </Row>
          <Spin spinning={cargandoTipos}>
            <Select
              style={{ width: '100%' }}
              size="large"
              placeholder="Busca o selecciona el tipo de datos..."
              value={tipo}
              onChange={handleTipoChange}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={opcionesTipo.map(op => ({ value: op.value, label: op.label }))}
            />
          </Spin>
        </Card>

        {/* Paso 2: descargar plantilla */}
        <Card
          size="small"
          style={{
            borderRadius: 12,
            border: tipo ? '1.5px solid #d9d9d9' : '1.5px dashed #e8e8e8',
            opacity: tipo ? 1 : 0.55,
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Row align="middle" justify="space-between" gutter={[16, 12]}>
            <Col>
              <Row align="middle" gutter={12}>
                <Col>
                  <DownloadOutlined style={{ fontSize: 20, color: '#faad14' }} />
                </Col>
                <Col>
                  <Text strong style={{ display: 'block' }}>2. Descarga la plantilla CSV</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Usa el formato correcto antes de importar
                  </Text>
                </Col>
              </Row>
            </Col>
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={!tipo}
                loading={descargar.isLoading}
                style={{
                  borderRadius: 20,
                  borderColor: '#faad14',
                  color: tipo ? '#faad14' : undefined,
                }}
              >
                Descargar plantilla
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Paso 3: subir archivo */}
        <Card
          size="small"
          style={{
            borderRadius: 12,
            border: tipo ? '1.5px solid #d9d9d9' : '1.5px dashed #e8e8e8',
            opacity: tipo ? 1 : 0.55,
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
            <Col>
              <UploadOutlined style={{ fontSize: 20, color: '#52c41a' }} />
            </Col>
            <Col flex="auto">
              <Text strong>3. Sube tu archivo CSV</Text>
            </Col>
          </Row>

          {!tipo && (
            <Alert
              message="Selecciona primero el tipo de archivo"
              type="warning"
              showIcon
              style={{ marginBottom: 12, borderRadius: 8 }}
            />
          )}

          <Dragger
            beforeUpload={beforeUpload}
            accept=".csv"
            fileList={archivo ? [archivo] : []}
            onRemove={() => setArchivo(null)}
            disabled={!tipo}
            maxCount={1}
            style={{ borderRadius: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 36, color: tipo ? '#52c41a' : '#d9d9d9' }} />
            </p>
            <p className="ant-upload-text" style={{ fontWeight: 500 }}>
              {tipo ? 'Clic o arrastra el archivo CSV aquí' : 'Selecciona primero el tipo de archivo'}
            </p>
            <p className="ant-upload-hint" style={{ color: '#bfbfbf' }}>
              Solo se acepta un archivo CSV a la vez
            </p>
          </Dragger>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Button
              type="primary"
              onClick={handleUpload}
              disabled={!tipo || !archivo}
              loading={importar.isLoading}
              icon={<UploadOutlined />}
              size="large"
              style={{
                borderRadius: 25,
                paddingLeft: 28,
                paddingRight: 28,
                height: 44,
              }}
            >
              Importar archivo
            </Button>
          </div>
        </Card>

      </Space>
    </div>
  );
};

export default CargaArchivos;
